/**
 * The signed-in draft (SCRUM-728): the unsaved document of someone who IS signed in, kept in the
 * browser so it outlives a dropped connection and a forced sign-out.
 *
 * Why it exists: the token lives a day, a 401 anywhere calls `forceLogout` (`api/http.ts`), the editor
 * unmounts, and the work in memory is gone. It happened to the owner on 11.10.2026 and to Viktor on
 * 10.10.2026 (his rider vanished).
 *
 * A sibling of `guest-draft.ts`, NOT a rewrite of it, because the ownership rule is the opposite one.
 * The guest's draft dies with his session (`sessionStorage`, the owner's words of 20.08.2026, see that
 * file). This one must survive closing the tab, so it lives in `localStorage` — and `localStorage`
 * outlives the VISITOR too, which already once put a stranger's stage plot in the owner's editor. Hence
 * the user id is part of the key, is checked again inside the stored envelope, and is what an explicit
 * Logout clears by.
 *
 * Decisions fixed by measurement (SCRUM-728), each visible below:
 *  - `localStorage`, not IndexedDB: `writeSignedInDraft` is called from `renderAll`, which also runs for
 *    pure-UI renders; an asynchronous store would have to be awaited there.
 *  - The DOCUMENT only, never the undo history: a rider may be 8 MiB (`MAX_DOCUMENT_SIZE_BYTES`) and the
 *    history holds up to 50 snapshots of it, against a ~5 MiB quota.
 *  - The envelope carries the server version the document was based on (`baseUpdatedAt`), so the editor
 *    can tell "continue" from "the server has moved on" (`classifyDraft`) and never restores silently.
 *  - The same crash quarantine as the guest draft: a mark before the restored document is rendered,
 *    removed right after; a mark found on the next load means that draft is killed, not retried.
 *
 * Nothing here ever throws: private browsing, a full quota, a forbidden store all degrade to "no
 * draft", never to a broken editor.
 */

import type { Rider } from "@scb/schema";

const PREFIX = "scb.draft.";
const APPLYING_PREFIX = "scb.draftApplying.";
/** `riderId === null` (not saved yet) is stored under this segment. */
const NEW_RIDER_SEGMENT = "new";

/** A runaway document must not eat the origin's quota that the theme and print options share. Counted
 *  in characters; the quota is ~5 MiB and some engines count two bytes per character. */
const MAX_DRAFT_CHARS = 1_500_000;

export interface SignedInDraft {
  riderId: string | null;
  /** The server's `updatedAt` of the document this edit started from; null for a never-saved rider. */
  baseUpdatedAt: string | null;
  /** ISO time the draft was last written — shown to the person as "unsaved changes from …". */
  savedAt: string;
  rider: Rider;
}

/**
 * SCRUM-883: имя «владельца» для гостя, закрывшего вкладку. Гостевой черновик (`guest-draft.ts`)
 * живёт в `sessionStorage` и умирает с вкладкой — это правило владельца SCRUM-300, и оно остаётся.
 * Но владелец 28.10.2026: «гость закрыл вкладку и вернулся — возвращаются объекты», а на вопрос,
 * подставлять ли молча: «пусть будет плашка и объекты». Поэтому рядом с сессионным черновиком лежит
 * ВТОРАЯ копия в этом же хранилище, под этим именем; на следующем входе она возвращается сама, а
 * плашка (`editor.draft.restored`) говорит, что это произошло.
 * Не совпадает ни с одним id пользователя: те — uuid.
 */
export const GUEST_DRAFT_OWNER = "guest";

export function signedInDraftKey(userId: string, riderId: string | null): string {
  return `${PREFIX}${userId}.${riderId ?? NEW_RIDER_SEGMENT}`;
}

const applyingKey = (userId: string): string => `${APPLYING_PREFIX}${userId}`;

const store = (): Storage => localStorage;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Same pruning as the guest draft: a non-object entry in `children`/`sets` crashes `nodeSig`. */
function pruneMalformedNodes(node: Record<string, unknown>): void {
  for (const field of ["children", "sets"] as const) {
    const list = node[field];
    if (!Array.isArray(list)) continue;
    const kept = list.filter(isPlainObject);
    node[field] = kept;
    for (const child of kept) pruneMalformedNodes(child);
  }
}

/** Serialisation of the last write per key, so a pure-UI render costs no storage traffic. */
const lastWritten = new Map<string, string>();

export type SignedInDraftWriteResult = "written" | "unchanged" | "failed";

/** Persists the draft. Must stay cheap and must never interrupt the edit it records: a dropped write
 *  only means the edit is not protected across a reload, which `"failed"` lets the caller say once. */
export function writeSignedInDraft(
  userId: string,
  riderId: string | null,
  baseUpdatedAt: string | null,
  rider: Rider,
  now: Date = new Date()
): SignedInDraftWriteResult {
  try {
    const key = signedInDraftKey(userId, riderId);
    const doc = JSON.stringify(rider);
    const fingerprint = `${baseUpdatedAt ?? ""}\n${doc}`;
    // "Unchanged" must also be true of the STORE: another tab's Logout, or the browser clearing site
    // data, removes the key behind this cache's back, and trusting the cache then would skip the write.
    if (lastWritten.get(key) === fingerprint && store().getItem(key) !== null) return "unchanged";
    const envelope = JSON.stringify({
      v: 1,
      userId,
      riderId,
      baseUpdatedAt,
      savedAt: now.toISOString(),
      rider: JSON.parse(doc) as unknown,
    });
    if (envelope.length > MAX_DRAFT_CHARS) return "failed";
    store().setItem(key, envelope);
    lastWritten.set(key, fingerprint);
    return "written";
  } catch {
    return "failed";
  }
}

/** The stored draft of THIS user for this rider, or undefined (none, corrupt, someone else's, no store). */
export function peekSignedInDraft(userId: string, riderId: string | null): SignedInDraft | undefined {
  try {
    const raw = store().getItem(signedInDraftKey(userId, riderId));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed) || parsed.userId !== userId) return undefined;
    const rider = parsed.rider;
    if (!isPlainObject(rider) || !Array.isArray(rider.children)) return undefined;
    pruneMalformedNodes(rider);
    return {
      riderId,
      baseUpdatedAt: typeof parsed.baseUpdatedAt === "string" ? parsed.baseUpdatedAt : null,
      savedAt: typeof parsed.savedAt === "string" ? parsed.savedAt : "",
      rider: rider as unknown as Rider,
    };
  } catch {
    return undefined;
  }
}

export type DraftOffer = "continue" | "diverged";

/** "continue" only when the server document is the very version the draft was started from. */
export function classifyDraft(draft: SignedInDraft, serverUpdatedAt: string | null): DraftOffer {
  return draft.baseUpdatedAt === serverUpdatedAt ? "continue" : "diverged";
}

/** Forgets one rider's draft — after a successful save, or when the person chose to discard it. */
export function clearSignedInDraft(userId: string, riderId: string | null): void {
  const key = signedInDraftKey(userId, riderId);
  lastWritten.delete(key);
  try {
    store().removeItem(key);
  } catch {
    /* nothing to do */
  }
}

/** Forgets every draft of one user and their crash mark — the explicit Logout. Not the forced one. */
export function clearAllSignedInDrafts(userId: string): void {
  const prefix = `${PREFIX}${userId}.`;
  for (const key of [...lastWritten.keys()]) if (key.startsWith(prefix)) lastWritten.delete(key);
  try {
    const s = store();
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (key !== null && key.startsWith(prefix)) doomed.push(key);
    }
    for (const key of doomed) s.removeItem(key);
    s.removeItem(applyingKey(userId));
  } catch {
    /* a store we cannot enumerate holds nothing we can show anyone either */
  }
}

/** Set immediately before a restored document is rendered; the value names the draft being applied. */
export function markSignedInDraftApplying(userId: string, riderId: string | null): void {
  try {
    store().setItem(applyingKey(userId), riderId ?? NEW_RIDER_SEGMENT);
  } catch {
    /* a store that cannot hold the flag cannot hold the draft either */
  }
}

/** Called immediately after that render finished without throwing. */
export function confirmSignedInDraftApplied(userId: string): void {
  try {
    store().removeItem(applyingKey(userId));
  } catch {
    /* best effort */
  }
}

/**
 * True when the previous attempt to apply a draft never reached `confirmSignedInDraftApplied` — that
 * draft crashed the render once and would again. It is deleted here (and only here), the mark with it.
 */
export function consumeCrashedSignedInDraft(userId: string): boolean {
  try {
    const marked = store().getItem(applyingKey(userId));
    if (marked === null) return false;
    store().removeItem(applyingKey(userId));
    clearSignedInDraft(userId, marked === NEW_RIDER_SEGMENT ? null : marked);
    return true;
  } catch {
    return false;
  }
}
